import { useContext, useEffect, useRef, useState } from "react";
import { ComponentTypes, ISettings } from "../../Pages/Editor/types";
import { PureCampaignBuilder } from "@/Apps/Funnels/CampaignBuilder/PureCampaignBuilder";
import Markdown from "react-markdown";
import { markdownComponents } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import { PagePreviewNode } from "../LangGraphBot/CampaignAgent.stories";
import { MobileState } from "@/Apps/Pages/MobileResponsiveToggle/types";
import PureCanvas from "@/Apps/Pages/V1Editor/PureCanvas";
import { PageContext } from "@/Apps/Pages/Page/PageContext";
import { addBodyIfNone } from "./PageBuilderArtifact";
import { cleanFromLangGraph } from "../LangGraphBot/LangGraphBot.stories";
import {
  cleanThisContent,
  recursivelyRetrieveItem,
} from "@/Apps/Pages/V2Editor/actions";
import { PageArtifactContainer } from "../LangGraphBot/components/PageArtifactContainer";
import { FullEditor } from "@/Apps/Pages/Editor/FullEditor";
import { ArtifactHeader } from "./ArtifactHeader";
import { AgentMode } from "../AgentModeButton";
import Page from "@/Apps/Pages/Page/Page";
import { Topbar, TopbarButton } from "@/ui/Layout/Topbar/Topbar";
import { Plus } from "lucide-react";
import { PaintBrush, PenNib, TreeStructure } from "@phosphor-icons/react";
import { Loading } from "@/ui/Layout/Loading";
import { AppContext } from "@/AppContext";

interface RenderArtifactContainer {
  type: "page_builder" | "campaign_builder" | "content_writer";
  content: ISettings[] | any[];
  mode: AgentMode;
  hasChildren: boolean;
  options: any;
  setOptions: (options: any) => void;
  thread: any;
  pageSpec?: any;
}

export const RenderArtifactContainer = ({
  type,
  // content = [],
  mode,
  hasChildren,
  options,
  setOptions = () => {},
  thread,
  pageSpec,
}: RenderArtifactContainer) => {
  const [currentContent, setCurrentContent] = useState(
    options?.container?.content || []
  );
  const [currentPageContent, setCurrentPageContent] = useState(
    options?.container?.pageContent || []
  );
  const [currentCampaignContent, setCurrentCampaignContent] = useState(
    options?.container?.campaignContent || []
  );
  const [canSave, setCanSave] = useState(false);
  const [isExplicitlyClosed, setIsExplicitlyClosed] = useState(false);

  const [isWidgetSidebarShowing, setIsWidgetSidebarShowing] = useState(false);
  const [isPropertiesSidebarShowing, setIsPropertiesSidebarShowing] =
    useState(true);
  const { setCopilotContext } = useContext(AppContext);

  useEffect(() => {
    // Don't update content if user explicitly closed the container
    if (isExplicitlyClosed) {
      return;
    }

    if (
      options?.container?.content &&
      options?.container?.content !== currentContent
    ) {
      setCurrentContent(options?.container?.content);
    }
    if (
      options?.container?.pageContent &&
      JSON.stringify(cleanThisContent(options?.container?.pageContent)) !==
        JSON.stringify(cleanThisContent(currentPageContent))
    ) {
      setCurrentPageContent(cleanThisContent(options?.container?.pageContent));
    }
    if (
      options?.container?.campaignContent &&
      options?.container?.campaignContent !== currentCampaignContent
    ) {
      setCurrentCampaignContent(options?.container?.campaignContent);
    }
  }, [
    options?.container?.content,
    options?.container?.pageContent,
    options?.container?.campaignContent,
    isExplicitlyClosed,
  ]);

  // Reset explicitly closed state when a new container appears
  useEffect(() => {
    if (options?.container && isExplicitlyClosed) {
      setIsExplicitlyClosed(false);
    }
  }, [options?.container?.type, options?.container?.title]);

  // Set canSave to true when container content changes (but not if explicitly closed)
  useEffect(() => {
    if (options?.container && !isExplicitlyClosed) {
      setCanSave(true);
    }
  }, [
    options?.container?.pageContent,
    options?.container?.content,
    options?.container?.campaignContent,
    isExplicitlyClosed,
  ]);

  // If type is "campaign_builder", fit the view after content loads
  useEffect(() => {
    if (
      type === "campaign_builder" &&
      options?.ref?.current &&
      typeof options?.ref?.current?.fitView === "function"
    ) {
      // Throttle fitView to only trigger once per half second
      let lastCalled = (window as any).__campaignBuilderFitViewLastCalled || 0;
      const now = Date.now();
      if (now - lastCalled > 500) {
        options?.ref?.current?.fitView();
        (window as any).__campaignBuilderFitViewLastCalled = now;
      }
    }
  }, [type, options?.ref?.current]);

  useEffect(() => {
    if (
      options?.container?.pageContent &&
      options?.container?.pageContent.length
    ) {
      setCanSave(true);
    }
  }, [options?.container?.pageContent, options?.container?.pageContent?.length]);

  return (
    <div className="h-full w-full">
      <style>
        {`
          @keyframes throb {
            0% {
              // background-color: rgba(0, 165, 3, 0.07);
              background-color: rgb(176, 226, 255, 0.07);
            }
            50% {
              background-color: rgb(176, 226, 255, 0.28);
            }
            100% {
              background-color: rgb(176, 226, 255, 0.07);
            }
          }
          .animate-throb {
            animation: throb 5s infinite;
          }
        `}
      </style>
      {type === "page_builder" && (
        <div className="h-full w-full overflow-y-hidden">
          <ArtifactHeader
            artifactOptions={options}
            setArtifactOptions={setOptions}
            hasChildren={hasChildren}
            mode={mode}
            topBarSettings={{
              page: [
                { label: "Launch Pages", href: "/pages" },
                {
                  label: options?.container?.title || "Page Builder",
                  href: "#",
                },
              ],
              leftIcon: (
                <>
                  {/* <div className="p-2" /> */}
                  <TopbarButton
                    onClick={() =>
                      setIsWidgetSidebarShowing(!isWidgetSidebarShowing)
                    }
                    isActive={isWidgetSidebarShowing}
                  >
                    <Plus />
                  </TopbarButton>
                </>
              ),
            }}
            thread={thread}
            onClose={() => {
              setCurrentPageContent([]);
              setCanSave(false);
              setIsExplicitlyClosed(true);
              setOptions({
                ...options,
                container: undefined,
                isShowing: hasChildren,
              });
            }}
          />
          <div className="h-full w-full overflow-y-auto pt-16 bg-white dark:bg-black/90 backdrop-blur">
            {/* <div className="bg-yellow-500 p-3 text-center text-black">
              There are{" "}
              <span className="font-bold">{currentPageContent.length}</span>{" "}
              total items in the current content array.
            </div> */}
            {/* {JSON.stringify(currentPageContent)} */}
            {/* {currentContent.length > 0 && currentContent?.find((itm: any) => itm.type === ComponentTypes.BODY) && <PureCanvas
              content={addBodyIfNone(
                cleanThisContent(cleanFromLangGraph(currentContent))
              )}
              onSave={({ content = [] }: { content?: ISettings[] }) => {
                // console.log("content", content);
                // const newContent = addBodyIfNone(cleanThisContent(content));
                // setRevisedObjects(newContent);
                // setTextContent(JSON.stringify(newContent, null, 2));
              }}
              onItemSelect={(id, settings) => {
                // setTargetIds([id]);
              }}
              onChange={() => {}}
              disableDefaultProperties
              setActiveAddNewTab={() => {}}
              mobileState={{ type: MobileState.FULLSCREEN }}
              setMobileState={() => {}}
              onLoad={() => {}}
            />} */}

            {currentPageContent.length > 1 &&
            currentPageContent?.find(
              (itm: any) => itm.type === ComponentTypes.BODY
            ) ? (
              <PageContext.Provider
                value={{
                  activeContent: currentPageContent,
                  setContent: setCurrentPageContent,
                  campaignSettings: pageSpec || { variables: [] },
                  parseLiquid: true,
                }}
              >
                <div className="bg-white">
                  <FullEditor
                    ref={options?.ref}
                    campaignId=""
                    pageId=""
                    objectId=""
                    pageContent={addBodyIfNone(
                      cleanThisContent(currentPageContent)
                    )}
                    mobileState={{ type: MobileState.FULLSCREEN }}
                    sidebarSettings={{
                      floatSidebars: true,
                      isWidgetSidebarShowing: isWidgetSidebarShowing,
                      isPropertiesSidebarShowing: isPropertiesSidebarShowing,
                      setIsWidgetSidebarShowing: setIsWidgetSidebarShowing,
                      setIsPropertiesSidebarShowing:
                        setIsPropertiesSidebarShowing,
                    }}
                    onLoad={() => {}}
                    onSave={({ content }: { content: ISettings[] }) => {
                      if (
                        JSON.stringify(cleanThisContent(content)) !==
                        JSON.stringify(cleanThisContent(currentPageContent))
                      ) {
                        console.log({ canSave, isExplicitlyClosed });
                        if (canSave && !isExplicitlyClosed) {
                          // only update if there is already content and user hasn't explicitly closed
                          setCurrentPageContent(cleanThisContent(content));
                          setOptions({
                            ...options,
                            container: {
                              ...options.container,
                              pageContent: cleanThisContent(content),
                            },
                          });
                        }
                      }
                    }}
                    setMobileState={() => {}}
                  />
                </div>
              </PageContext.Provider>
            ) : (
              // <PageArtifactContainer
              //   pageContent={addBodyIfNone(
              //     cleanThisContent(cleanFromLangGraph(content)) // currentContent
              //   )}
              //   pageSpec={options?.container?.payload?.pageSpec || {}}
              //   onSave={({ content }) => {
              //     setOptions({
              //       ...options,
              //       container: {
              //         type: "page_builder",
              //         content: currentContent,
              //         setOptions,
              //       },
              //       graphState: {
              //         ...options.graphState,
              //         pageContent: currentContent,
              //       },
              //     });
              //   }}
              //   // onChange={({ content }) => {
              //   //   setCurrentContent(content);
              //   // }}
              // />
              <div className="h-full flex flex-col items-center justify-center bg-white/80 dark:bg-black/80 backdrop-blur">
                <Loading type="gallery" className="w-[500px]" />
                <div className="text-center text-black/50 dark:text-white/50 text-xl mt-4">
                  Building your page...
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {type === "campaign_builder" && (
        <div className="h-full w-full overflow-y-auto bg-white dark:bg-black backdrop-blur">
          <ArtifactHeader
            artifactOptions={options}
            setArtifactOptions={setOptions}
            hasChildren={hasChildren}
            mode={mode}
            thread={thread}
            topBarSettings={{
              page: [
                { label: "Launch Funnels", href: "/funnels" },
                {
                  label: options?.container?.title || "Campaign Builder",
                  href: "#",
                },
              ],
              leftIcon: (
                <>
                  <div className="pl-0" />
                  <TreeStructure />
                </>
              ),
            }}
            onClose={() => {
              setIsExplicitlyClosed(true);
              setOptions({
                ...options,
                container: undefined,
                isShowing: hasChildren,
              });
            }}
          />
          {/* {JSON.stringify(currentCampaignContent)} */}
          {currentCampaignContent.length > 0 &&
            currentCampaignContent?.find((itm: any) =>
              Object.keys(itm).includes("connectTo")
            ) && (
              <div className="h-[90%]">
                <PureCampaignBuilder
                  ref={options?.ref}
                  id="campaign-builder"
                  objects={currentCampaignContent}
                  onChange={() => {}}
                  deleteConnection={() => {}}
                  updateObject={() => {}}
                  deleteObject={() => {}}
                  createConnection={() => {}}
                  createObject={() => {}}
                  onNodeDoubleClick={(e, node) => {
                    console.log("onNodeDoubleClick", e, node);
                    const { connectTo, content, id, label, name, type } =
                      node?.data || {};
                    if (type === "PageComponent") {
                      setOptions({
                        ...options,
                        container: {
                          ...options.container,
                          type: "page_builder",
                          pageContent: addBodyIfNone(
                            cleanThisContent(cleanFromLangGraph(content))
                          ),
                          title: name,
                          setOptions,
                        },
                      });
                    }
                  }}
                  CustomNode={PagePreviewNode}
                />
              </div>
            )}
        </div>
      )}

      {type === "content_writer" && (
        <div className="h-full w-full overflow-y-auto bg-black">
          <ArtifactHeader
            artifactOptions={options}
            setArtifactOptions={setOptions}
            hasChildren={hasChildren}
            mode={mode}
            thread={thread}
            topBarSettings={{
              page: [
                { label: "Launch Content", href: "/content" },
                {
                  label: options?.container?.title || "Content Writer",
                  href: "#",
                },
              ],
              leftIcon: (
                <>
                  <div className="pl-0" />
                  <PenNib />
                </>
              ),
            }}
            onClose={() => {
              setIsExplicitlyClosed(true);
              setOptions({
                ...options,
                container: undefined,
                isShowing: hasChildren,
              });
            }}
          />
          <div className="p-9 mt-16 h-full w-full overflow-y-auto">
            <Markdown
              children={String(currentContent[0]?.content)}
              remarkPlugins={[remarkBreaks]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            />
          </div>
        </div>
      )}
    </div>
  );
};
